import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  Typography,
  Button,
  Space,
  Row,
  Col,
  Card,
  Divider,
  App,
} from "antd";
import {
  ArrowRightOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  RiseOutlined,
  CheckCircleOutlined,
  UserOutlined,
  LockOutlined,
  BarChartOutlined,
  LoginOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosConfig";

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;

export default function Homepage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(localStorage.getItem("isAuthenticated") === "true");
    };
    checkAuth();
    window.addEventListener("storage", checkAuth);
    return () => window.removeEventListener("storage", checkAuth);
  }, []);

  const handleExplore = () => {
    if (isAuthenticated) {
      navigate("/dashboard");
    } else {
      navigate("/login");
    }
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post("/api/user/logout", {});
      message.success("Logged out successfully!", 3);
    } catch (err) {
      console.log(
        "Logout endpoint not available, performing client-side logout"
      );
      message.info("Logged out", 3);
    } finally {
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("apiKey");
      localStorage.removeItem("api_credits");
      localStorage.removeItem("api_credits_last_refill");
      setIsAuthenticated(false);
      navigate("/");
      window.location.reload();
    }
  };

  const features = [
    {
      icon: <SafetyOutlined style={{ fontSize: "48px" }} />,
      title: "Speed & Security",
      description:
        "Enterprise-grade security with lightning-fast verification processes",
    },
    {
      icon: <ThunderboltOutlined style={{ fontSize: "48px" }} />,
      title: "Flexibility & Scalability",
      description: "Modular API stack that scales with your business needs",
    },
    {
      icon: <RiseOutlined style={{ fontSize: "48px" }} />,
      title: "Better Conversion",
      description:
        "Streamlined workflows that reduce drop-offs and improve user experience",
    },
  ];

  const offerings = [
    {
      title: "Identity Verification APIs",
      description:
        "Instant, compliant KYC checks across all verification documents. Verify PAN, Aadhaar, and more in real-time with seamless fallback logic.",
    },
    {
      title: "AI-Powered Fraud Detection",
      description:
        "Advanced AI to stop fraud before it starts. Detect impersonation, deepfakes, and tampered documents with liveness checks.",
    },
    {
      title: "Credit Risk & Underwriting",
      description:
        "Holistic risk insights in a single API layer. Access comprehensive signals to evaluate ability and intent to pay.",
    },
    {
      title: "Orchestration & Automation",
      description:
        "Simplify workflows and scale with confidence. Combine, route, and automate multiple checks in a single API journey.",
    },
  ];

  const benefits = [
    {
      icon: <UserOutlined style={{ fontSize: "48px" }} />,
      title: "Comprehensive Toolkit",
      description: "All the tools you need in one integrated platform",
    },
    {
      icon: <LockOutlined style={{ fontSize: "48px" }} />,
      title: "Secure & Scalable",
      description: "Enterprise-grade security with unlimited scalability",
    },
    {
      icon: <BarChartOutlined style={{ fontSize: "48px" }} />,
      title: "Seamless Integration",
      description: "Easy integration with your existing systems",
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh", background: "linear-gradient(135deg, #f0f4ff 0%, #e6f2ff 100%)" }}>
      <Header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
          boxShadow: "0 4px 16px rgba(0, 102, 255, 0.08)",
          padding: "0 32px",
          position: "sticky",
          top: 0,
          zIndex: 100,
          borderBottom: "1px solid #e1e8f0",
        }}
      >
        <Title
          level={3}
          style={{
            margin: 0,
            background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            cursor: "pointer",
            fontWeight: 700,
            letterSpacing: "-0.5px",
          }}
          onClick={() => navigate("/")}
        >
          VerifyU
        </Title>
        <Space>
          {isAuthenticated ? (
            <>
              <Button type="link" onClick={() => navigate("/dashboard")}>
                Dashboard
              </Button>
              <Button
                type="primary"
                danger
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                style={{
                  borderRadius: 8,
                  fontWeight: 600,
                  boxShadow: "0 2px 8px rgba(255, 51, 102, 0.2)",
                }}
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button type="link" onClick={() => navigate("/login")}>
                Login
              </Button>
              <Button
                type="primary"
                icon={<ArrowRightOutlined />}
                onClick={handleExplore}
                style={{
                  borderRadius: 8,
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0, 102, 255, 0.3)",
                }}
              >
                Explore
              </Button>
            </>
          )}
        </Space>
      </Header>

      <Content>
        {/* Hero Section */}
        <div
          style={{
            padding: "100px 24px",
            textAlign: "center",
            background: "linear-gradient(135deg, #0066ff 0%, #0052cc 50%, #003d99 100%)",
            color: "white",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(circle at 20% 50%, rgba(0, 217, 255, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0, 102, 255, 0.15) 0%, transparent 50%)",
              pointerEvents: "none",
            }}
          />
          <Space orientation="vertical" size="large" style={{ width: "100%", position: "relative", zIndex: 1 }}>
            <Title
              level={1}
              style={{
                color: "white",
                fontSize: "56px",
                margin: 0,
                fontWeight: 700,
                textShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
              }}
            >
              VerifyU: Empowering India's{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #00d9ff 0%, #ffffff 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                BFSI Ecosystem
              </span>
            </Title>
            <Paragraph
              style={{
                color: "rgba(255, 255, 255, 0.95)",
                fontSize: "20px",
                maxWidth: "900px",
                margin: "0 auto",
                lineHeight: 1.7,
                textShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              Leading digital infrastructure provider for India's BFSI
              ecosystem, specializing in API-first onboarding and
              underwriting microservices. Our modular API stack enables
              seamless workflows across KYC, fraud detection, credit risk,
              and decisioning.
            </Paragraph>
            <Space size="middle" style={{ marginTop: "32px" }}>
              <Button
                type="primary"
                size="large"
                icon={<ArrowRightOutlined />}
                onClick={handleExplore}
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)",
                  color: "#0066ff",
                  border: "none",
                  fontWeight: 600,
                  height: "48px",
                  padding: "0 32px",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
                }}
              >
                {isAuthenticated ? "Go to Dashboard" : "Get Started"}
              </Button>
              {!isAuthenticated && (
                <Button
                  size="large"
                  onClick={() => navigate("/login")}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    height: "48px",
                    padding: "0 32px",
                  }}
                >
                  Admin Login
                </Button>
              )}
            </Space>
          </Space>
        </div>

        {/* Stats Section */}
        <div style={{ padding: "80px 24px", background: "#ffffff" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "64px" }}>
              <Title level={2} style={{ color: "#0a1629", marginBottom: "16px", fontWeight: 700 }}>
                1 Million+ Customers Verified
              </Title>
              <Paragraph style={{ color: "#6b7a99", fontSize: "18px" }}>
                Proven at scale, our AI ensures smooth onboarding across
                diverse devices and demographics
              </Paragraph>
            </div>
            <Row gutter={[32, 32]}>
              {features.map((feature, index) => (
                <Col xs={24} md={8} key={index}>
                  <Card
                    hoverable
                    style={{
                      textAlign: "center",
                      borderRadius: 16,
                      boxShadow: "0 4px 16px rgba(0, 102, 255, 0.08)",
                      border: "1px solid #e1e8f0",
                      height: "100%",
                    }}
                    styles={{ body: { padding: "32px" } }}
                  >
                    <div
                      style={{
                        color: "#0066ff",
                        marginBottom: "24px",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {feature.icon}
                    </div>
                    <Title level={4} style={{ color: "#0a1629", fontWeight: 600 }}>
                      {feature.title}
                    </Title>
                    <Text style={{ color: "#6b7a99", fontSize: "15px" }}>
                      {feature.description}
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        {/* Offerings Section */}
        <div style={{ padding: "80px 24px", background: "#f0f4ff" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "64px" }}>
              <Title level={2} style={{ color: "#0a1629", marginBottom: "16px", fontWeight: 700 }}>
                Our Offerings
              </Title>
              <Paragraph style={{ color: "#6b7a99", fontSize: "18px" }}>
                Designed to streamline operations and enhance
                decision-making processes for financial institutions,
                ensuring seamless and compliant workflows.
              </Paragraph>
            </div>

            <Row gutter={[32, 32]}>
              {offerings.map((offering, index) => (
                <Col xs={24} md={12} key={index}>
                  <Card
                    hoverable
                    style={{
                      borderRadius: 16,
                      boxShadow: "0 4px 16px rgba(0, 102, 255, 0.08)",
                      border: "1px solid #e1e8f0",
                      height: "100%",
                    }}
                    styles={{ body: { padding: "32px" } }}
                  >
                    <Space align="start">
                      <div
                        style={{
                          background: "linear-gradient(135deg, rgba(0, 102, 255, 0.1) 0%, rgba(0, 217, 255, 0.1) 100%)",
                          padding: "16px",
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircleOutlined
                          style={{ fontSize: "28px", color: "#0066ff" }}
                        />
                      </div>
                      <Space orientation="vertical" size="small">
                        <Title level={4} style={{ color: "#0a1629", margin: 0, fontWeight: 600 }}>
                          {offering.title}
                        </Title>
                        <Text style={{ color: "#6b7a99", fontSize: "15px" }}>
                          {offering.description}
                        </Text>
                      </Space>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        {/* Benefits Section */}
        <div style={{ padding: "80px 24px", background: "#ffffff" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "64px" }}>
              <Title level={2} style={{ color: "#0a1629", marginBottom: "16px", fontWeight: 700 }}>
                Tailored for Excellence
              </Title>
              <Paragraph style={{ color: "#6b7a99", fontSize: "18px" }}>
                Fidaro's solutions are meticulously crafted to meet the
                diverse needs of the BFSI ecosystem, ensuring that our
                clients have the tools they need to thrive.
              </Paragraph>
            </div>

            <Row gutter={[32, 32]}>
              {benefits.map((benefit, index) => (
                <Col xs={24} md={8} key={index}>
                  <Card
                    hoverable
                    style={{
                      textAlign: "center",
                      borderRadius: 16,
                      boxShadow: "0 4px 16px rgba(0, 102, 255, 0.08)",
                      border: "1px solid #e1e8f0",
                      height: "100%",
                    }}
                    styles={{ body: { padding: "32px" } }}
                  >
                    <div
                      style={{
                        color: "#0066ff",
                        marginBottom: "24px",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {benefit.icon}
                    </div>
                    <Title level={4} style={{ color: "#0a1629", fontWeight: 600 }}>
                      {benefit.title}
                    </Title>
                    <Text style={{ color: "#6b7a99", fontSize: "15px" }}>
                      {benefit.description}
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        {/* CTA Section */}
        <div style={{ padding: "80px 24px", textAlign: "center" }}>
          <Card
            style={{
              borderRadius: 20,
              background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
              border: "none",
              textAlign: "center",
              maxWidth: "1000px",
              margin: "0 auto",
            }}
            styles={{ body: { padding: "64px 48px" } }}
          >
            <Title level={2} style={{ color: "white", marginBottom: "24px", fontWeight: 700 }}>
              Unlock the potential of your institution
            </Title>
            <Paragraph
              style={{
                color: "rgba(255, 255, 255, 0.95)",
                fontSize: "18px",
                maxWidth: "800px",
                margin: "0 auto 32px auto",
              }}
            >
              Experience seamless workflows and enhanced performance with
              VerifyU's advanced digital infrastructure solutions.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              icon={<ArrowRightOutlined />}
              onClick={handleExplore}
              style={{
                background: "white",
                color: "#0066ff",
                borderColor: "white",
                fontWeight: 600,
                height: "48px",
                padding: "0 40px",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
              }}
            >
              Get in Touch
            </Button>
          </Card>
        </div>
      </Content>

      <Footer
        style={{
          textAlign: "center",
          background: "linear-gradient(135deg, #001529 0%, #002140 100%)",
          padding: "48px 24px",
          color: "rgba(255, 255, 255, 0.65)",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Row gutter={[32, 32]} justify="space-between" style={{ marginBottom: "32px" }}>
            <Col xs={24} md={8}>
              <Title
                level={3}
                style={{
                  color: "#0066ff",
                  margin: 0,
                  marginBottom: "16px",
                  fontWeight: 700,
                }}
              >
                VerifyU
              </Title>
              <Text style={{ color: "rgba(255, 255, 255, 0.65)" }}>
                Empowering India's BFSI Ecosystem with digital
                infrastructure solutions.
              </Text>
            </Col>
            <Col xs={24} md={8}>
              <Title level={5} style={{ color: "white", marginBottom: "16px", fontWeight: 600 }}>
                Quick Links
              </Title>
              <Space orientation="vertical">
                {isAuthenticated ? (
                  <>
                    <Button
                      type="link"
                      style={{ color: "rgba(255, 255, 255, 0.65)", padding: 0 }}
                      onClick={() => navigate("/dashboard")}
                    >
                      Dashboard
                    </Button>
                    <Button
                      type="link"
                      style={{ color: "rgba(255, 255, 255, 0.65)", padding: 0 }}
                      onClick={handleLogout}
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="link"
                      style={{ color: "rgba(255, 255, 255, 0.65)", padding: 0 }}
                      onClick={() => navigate("/login")}
                    >
                      Admin Login
                    </Button>
                    <Button
                      type="link"
                      style={{ color: "rgba(255, 255, 255, 0.65)", padding: 0 }}
                      onClick={handleExplore}
                    >
                      Explore
                    </Button>
                  </>
                )}
              </Space>
            </Col>
            <Col xs={24} md={8}>
              <Title level={5} style={{ color: "white", marginBottom: "16px", fontWeight: 600 }}>
                Contact
              </Title>
              <Space orientation="vertical">
                <Text style={{ color: "rgba(255, 255, 255, 0.65)" }}>
                  General Inquiries: info@verifyu.com
                </Text>
                <Text style={{ color: "rgba(255, 255, 255, 0.65)" }}>
                  Customer Care: care@verifyu.com
                </Text>
              </Space>
            </Col>
          </Row>
          <Divider style={{ borderColor: "rgba(255, 255, 255, 0.1)" }} />
          <Paragraph style={{ color: "rgba(255, 255, 255, 0.45)", marginTop: "32px" }}>
            © 2024 by VerifyU. All rights reserved.
          </Paragraph>
        </div>
      </Footer>
    </Layout>
  );
}
